<template>
  <div class="location-container">
    <!-- Loading State -->
    <div v-if="locationLoading" class="location-status loading">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Getting your location...</span>
    </div>

    <!-- Error State -->
    <div v-if="locationError" class="location-status error">
      <i class="fas fa-exclamation-triangle"></i>
      <div>
        <span class="error-title">Location Unavailable</span>
        <span class="error-message">{{ locationError }}</span>
      </div>
      <button @click="retryLocation" class="retry-btn">
        <i class="fas fa-sync-alt text-light"></i> Retry
      </button>
    </div>

    <!-- Success State -->
    <!-- <div v-if="hasLocation" class="location-display">
      <div class="location-pin">
        <i class="fas fa-map-marker-alt"></i>
      </div>
      <div class="coordinates">
        <div class="coordinate">
          <span class="label">Latitude:</span>
          <span class="value">{{ currentLocation.latitude.toFixed(6) }}</span>
        </div>
        <div class="coordinate">
          <span class="label">Longitude:</span>
          <span class="value">{{ currentLocation.longitude.toFixed(6) }}</span>
        </div>
      </div>
      <div class="location-actions">
        <button @click="copyCoordinates" class="action-btn" title="Copy coordinates">
          <i class="fas fa-copy"></i>
        </button>
        <button @click="openInMaps" class="action-btn" title="Open in maps">
          <i class="fas fa-map"></i>
        </button>
      </div>
    </div> -->
  </div>
</template>

<script setup>
import useGeolocation from "@/utils/useGeoLocation";
import { ref, watch, onMounted } from "vue";
const currentLocation = ref({ latitude: 0, longitude: 0 });
const locationLoading = ref(false);
const locationError = ref(null);
const hasLocation = ref(false);
const hasLocationError = ref(false);
/* eslint-disable */
const emit = defineEmits(["position-changed"]);
const {
  coords,
  error,
  isSupported,
  isTracking,
  getCurrentPosition,
  startTracking,
  stopTracking,
  lastGoodPosition,
} = useGeolocation({
  enableHighAccuracy: true,
  timeout: 10000, // Increased timeout
});

const retryCount = ref(0);
const maxRetries = 5;

const initLocation = async () => {
  locationLoading.value = true;
  hasLocationError.value = false;
  try {
    await getPositionWithRetry();
    startTracking();
    hasLocation.value = true;
  } catch (err) {
    hasLocationError.value = true;
    if (err.code === 1) {
      locationError.value = "Please allow user location access.";
    } else {
      locationError.value = err.message;
    }
    console.error("Failed to initialize location:", err);
  } finally {
    locationLoading.value = false;
  }
};

const retryLocation = () => {
  locationError.value = null;
  retryCount.value = 0;
  initLocation();
};

const getPositionWithRetry = async () => {
  while (retryCount.value < maxRetries) {
    try {
      //console.log("trying to get location...");
      const position = await getCurrentPosition();
      retryCount.value = 0;
      return position;
    } catch (err) {
      retryCount.value++;
      if (retryCount.value >= maxRetries) {
        hasLocationError.value = true;
        throw err;
      }
      // Exponential backoff
      await new Promise((resolve) =>
        setTimeout(resolve, 1000 * Math.pow(2, retryCount.value))
      );
    }
  }
};

const updateUserPosition = (pos) => {
  currentLocation.value = pos;
  emit("position-changed", pos);
};

watch(
  coords,
  (newCoords) => {
    if (newCoords) updateUserPosition(newCoords);
  },
  { deep: true }
);

onMounted(() => {
  initLocation();
});
</script>

<style scoped>
.location-container {
  font-family: "Segoe UI", system-ui, sans-serif;
  max-width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Status Messages */
.location-status {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border-left: 4px solid transparent;
}

.location-status i {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.loading {
  background: #f8f9fa;
  border-left-color: #4e9af1;
  color: #2c3e50;
}

.loading i {
  color: #4e9af1;
}

.error {
  background: #fff0f0;
  border-left-color: #ff4d4d;
  color: #5c1a1a;
}

.error i {
  color: #ff4d4d;
}

.error-title {
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
}

.error-message {
  font-size: 0.9rem;
  opacity: 0.8;
  display: block;
}

/* Location Display */
.location-display {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: white;
  border-top: 1px solid #f0f0f0;
}

.location-pin {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ff4d4d;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.location-pin i {
  font-size: 1.2rem;
}

.coordinates {
  flex-grow: 1;
}

.coordinate {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.coordinate:last-child {
  margin-bottom: 0;
}

.label {
  font-weight: 500;
  color: #555;
  min-width: 70px;
}

.value {
  font-family: "Roboto Mono", monospace;
  color: #333;
}

/* Buttons */
.retry-btn {
  margin-left: auto;
  background: #ff4d4d;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
}

.retry-btn:hover {
  background: #e04444;
}

.retry-btn i {
  font-size: 0.9rem;
}

.location-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f0f0f0;
  border: none;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  background: #e0e0e0;
  color: #333;
}

.action-btn i {
  font-size: 0.9rem;
}

/* Responsive */
@media (max-width: 480px) {
  .location-display {
    flex-wrap: wrap;
  }

  .location-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
